// App
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';

// Types
import type { SimilarListingDetails } from 'app/types/similarListingDetails.type';
import type { RootReduxState } from 'app/shared/flux/reducers';

// Lodash
import map from 'lodash/map';
import compact from 'lodash/compact';

// Actions
import ContactActions from 'app/shared/flux/actions/ContactActions';
import { logError } from '@zg-rentals/log-error';

// Components
import ContactSimilarListingsCheckboxWrapper from 'app/shared/modules/hdp/form/Contact/ContactSimilarListingsV2/ContactSimilarListingsCheckboxWrapper';
import Button from 'app/shared/core/Button';

// Misc / utils
import { getSimilarListingType, getTrackingFunction } from 'app/shared/utils/clickstreamUtils';
import {
  emitTrackSimilarListingMultiContactMessageButtonClickEvent,
  emitTrackSimilarListingMultiContactSubmitEvent,
} from 'app/shared/models/Clickstream/ContactClickstreamEvents';
import * as S from 'app/shared/modules/hdp/form/Contact/ContactSimilarListingsV2/styles';
import { buildContactParams, isValidSimilarListing } from './contactHelpers';

// Experiments
import { useExperiment } from 'app/shared/hooks/useExperiment';
import constants from 'app/shared/constants/ExperimentConstants';
import type { ListingDetails } from 'app/types/listingDetails.type';

const ContactSimilarListingsV2 = ({
  listing,
  similarListings,
}: {
  listing: ListingDetails;
  similarListings: Array<SimilarListingDetails>;
}) => {
  // Redux
  const dispatch = useDispatch();
  const cachedInquiry = useSelector((state: RootReduxState) => state.user.inquiryDataCache);
  const userToken = useSelector((state: RootReduxState) => state.user.userToken);

  // A/B Test: Similar Listings Auto-Select
  const isControl = useExperiment(constants.HOTPADS_WEB_SIMILAR_LISTINGS_AUTO_SELECT);

  const shownSimilarListings = similarListings;
  // Control: opt-in behavior (checkboxes start unchecked, null array)
  // Treatment: opt-out behavior (checkboxes start checked, pre-populated array)
  const initState: Array<SimilarListingDetails | null> = isControl
    ? new Array(shownSimilarListings.length).fill(null)
    : shownSimilarListings.slice();
  const [listingsToContact, setListingsToContact] = useState(initState);
  const [haveMessagesBeenSent, setHaveMessagesBeenSent] = useState(false);
  const [dynamicButtonText, setDynamicButtonText] = useState('Send messages');

  const handleCheckboxClick = (listing: SimilarListingDetails, idx: number, action: string) => {
    if (action === 'add') {
      const tempArr = listingsToContact.toSpliced(idx, 1, listing);
      setListingsToContact(tempArr);
    } else {
      const tempArr = listingsToContact.toSpliced(idx, 1, null);
      setListingsToContact(tempArr);
    }
  };

  const handleSendMessageButtonClick = async () => {
    const compactListingsToContact = compact(listingsToContact);
    const DEFAULT_INQUIRY_TEXT = "I'm interested in your property.";

    // Clickstream event 7276
    emitTrackSimilarListingMultiContactMessageButtonClickEvent({
      listing: listing,
      triggerLocation: 'home_details',
      triggerObject: 'home_details|post_contact_modal',
    });

    // Set state that messages have been sent
    setHaveMessagesBeenSent(true);

    // Change button text to read messages sent
    setDynamicButtonText('Messages sent');

    // Loop through each similar listing and submit a contact
    // IMPORTANT: Must use similarListing here, NOT the original listing prop
    // otherwise, using origin listing causes contacts to go to original property (or get filtered out)
    for await (const similarListing of compactListingsToContact) {
      try {
        // Safety check: don't contact the original listing (should never happen but prevents duplicates)
        if (!isValidSimilarListing(listing, similarListing)) {
          continue;
        }

        const rentalSubmitId = uuidv4();
        const listingType = getSimilarListingType(similarListing);

        // Build contact params using helper (tested in contactHelpers.test.ts)
        const contactParams = buildContactParams(similarListing, userToken, cachedInquiry, DEFAULT_INQUIRY_TEXT);

        await dispatch(
          ContactActions.submitContact({
            ...contactParams,
            rentalSubmitId,
          }),
        );

        // Clickstream event 5575, 5576, 5577 paid, free, paid-demoted
        // Must track similarListing to attribute lead to correct property
        getTrackingFunction({ type: listingType })?.({
          listing: similarListing as unknown as ListingDetails, // Type assertion safe: SimilarListingDetails has all required fields
          triggerLocation: 'home_details',
          triggerObject: 'home_details_component|modal_contact_form',
          triggerSource: 'button_to_send_message',
        });

        // Clickstream event 7283 - tracks each similar listing contact submission
        // Must use similarListing to record correct property in analytics
        emitTrackSimilarListingMultiContactSubmitEvent({
          listing: similarListing as unknown as ListingDetails, // Type assertion safe: SimilarListingDetails has all required fields
          rentalSubmitId,
          triggerLocation: 'home_details',
          triggerObject: 'home_details|post_contact_modal',
        });
      } catch (err) {
        logError({
          error: err,
          errorType: 'SimilarListingsContactError',
          context: 'Error submitting contact for similar listings in post contact modal',
        });
      }
    }
  };

  if (shownSimilarListings.length === 0) {
    return null;
  }

  const shouldButtonBeDisabled = haveMessagesBeenSent || compact(listingsToContact).length === 0;

  return (
    <article>
      <S.Divider />
      <S.ContactCtaArticle>
        <S.ContactTextHeading>Contact more properties</S.ContactTextHeading>
        <S.ContactTextBody>
          {"Let more properties know that you're interested. Select the properties and message them all at once."}
        </S.ContactTextBody>
        <Button onClick={handleSendMessageButtonClick} disabled={shouldButtonBeDisabled}>
          {dynamicButtonText}
        </Button>
      </S.ContactCtaArticle>
      <S.OuterScrollContainer>
        <S.ListContainer>
          {map(shownSimilarListings, (similarListing, idx) => {
            return (
              <S.ListItem key={`similar-listing-${idx}-${similarListing.aliasEncoded}`}>
                <ContactSimilarListingsCheckboxWrapper
                  listing={similarListing}
                  onClick={handleCheckboxClick}
                  idx={idx}
                  defaultChecked={!isControl}
                />
                <S.ListingWrapper
                  key={similarListing.aliasEncoded}
                  listing={similarListing}
                  triggerLocation="home_details"
                  triggerObject="home_details_component|post_contact_modal"
                />
              </S.ListItem>
            );
          })}
        </S.ListContainer>
      </S.OuterScrollContainer>
    </article>
  );
};

ContactSimilarListingsV2.propTypes = {
  similarListings: PropTypes.array,
};

export default ContactSimilarListingsV2;
